import { ReadOnlyItem, Row, SectionHeader } from '@ydistri/ds';
import React, { useMemo } from 'react';
import { styled } from 'styled-components';
import { SKU_ID_LABEL } from '../../../../../../components/global/_constants';
import LinkIcon from '../../../../../../components/global/Link/LinkIcon';
import { ProductResponse, StoreResponse } from '@ydistri/api-sdk';
import StoreIdentifications from '../../../../../../components/global/StoreIdentifications/StoreIdentifications';
import ProductIdOrCode from '../../../../../../components/global/ProductIdentifications/ProductIdentifications';
import { computeRemSize } from '@ydistri/theme';

const HeaderWrapper = styled(Row)`
  min-height: ${computeRemSize(26)};
`;

interface BaseProps {
  skuId?: number;
  link?: string;
  prefix?: string;
}

interface StoreProps extends BaseProps {
  type: 'store';
  entity: StoreResponse;
  /** See StoreIdentifications' storeDecileOverride - the per-calculation snapshot value. */
  storeDecileOverride?: string | null;
  /** See StoreIdentifications' brandQuintile. */
  brandQuintile?: number | null;
}

interface ProductProps extends BaseProps {
  type: 'product';
  entity: ProductResponse;
}

type CalculationRedistributionSectionHeaderProps = StoreProps | ProductProps;

const CalculationRedistributionSectionHeader: React.FC<
  CalculationRedistributionSectionHeaderProps
> = props => {
  const { type, entity, prefix, skuId, link } = props;
  const content = entity.name;

  const sectionHeaderContent = useMemo(() => {
    const sectionTitle = prefix ? `${prefix}: ${content}` : content;
    if (link) {
      return (
        <Row $alignItems="center" $gap="1rem">
          {sectionTitle}
          {link && <LinkIcon url={link} />}
        </Row>
      );
    } else {
      return sectionTitle;
    }
  }, [content, link, prefix]);

  return (
    <HeaderWrapper $gap="1rem" $alignItems="center">
      <SectionHeader>{sectionHeaderContent}</SectionHeader>
      {props.type === 'store' && (
        <StoreIdentifications
          store={props.entity}
          storeDecileOverride={props.storeDecileOverride}
          brandQuintile={props.brandQuintile}
        />
      )}
      {type === 'product' && <ProductIdOrCode product={entity} />}
      {skuId && (
        <ReadOnlyItem
          key="sku-id"
          label={SKU_ID_LABEL}
          value={skuId.toString()}
          spaceBetween="0.5rem"
        />
      )}
    </HeaderWrapper>
  );
};

export default CalculationRedistributionSectionHeader;
